// First-touch + latest-touch attribution, persisted first-party. No PII ever leaves here.
const UTMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid"];
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const ALLOWED_HOSTS = (process.env.REACT_APP_GA_ALLOWED_HOSTS || "collegeunlock.com,www.collegeunlock.com")
  .split(",").map((s) => s.trim()).filter(Boolean);

// Production analytics accepts only approved CollegeUnlock hostnames.
export function isApprovedHost() {
  try { return ALLOWED_HOSTS.includes(window.location.hostname); } catch { return false; }
}

function externalReferrer() {
  try {
    const ref = document.referrer || "";
    if (!ref) return "";
    return new URL(ref).hostname === window.location.hostname ? "" : ref;
  } catch { return ""; }
}

function snapshot() {
  const p = new URLSearchParams(window.location.search);
  const s = {
    landingPage: (window.location.pathname + window.location.search).slice(0, 300),
    referrer: externalReferrer().slice(0, 300),
    at: new Date().toISOString(),
  };
  [...UTMS, ...CLICK_IDS].forEach((k) => { const v = p.get(k); if (v) s[k] = v.slice(0, 200); });
  return s;
}

function hasCampaignSignal(s) {
  return [...UTMS, ...CLICK_IDS].some((k) => s[k]) || !!s.referrer;
}

function readJson(store, key) {
  try { return JSON.parse(store.getItem(key) || "null"); } catch { return null; }
}

// Call on every route change. First-touch is written once and NEVER overwritten;
// latest-touch refreshes once per session and whenever a new campaign signal appears.
export function captureTouch() {
  try {
    const snap = snapshot();
    if (!localStorage.getItem("cu_attr_first")) {
      localStorage.setItem("cu_attr_first", JSON.stringify(snap));
    }
    if (!sessionStorage.getItem("cu_attr_touched") || hasCampaignSignal(snap)) {
      sessionStorage.setItem("cu_attr_touched", "1");
      localStorage.setItem("cu_attr_last", JSON.stringify(snap));
    }
  } catch {}
}

// GA client/session identifiers (only where permitted: approved production hosts).
export function captureGaIds() {
  try {
    if (!isApprovedHost() || typeof window.gtag !== "function" || !GA_ID) return;
    window.gtag("get", GA_ID, "client_id", (v) => { if (v) sessionStorage.setItem("cu_ga_cid", String(v)); });
    window.gtag("get", GA_ID, "session_id", (v) => { if (v) sessionStorage.setItem("cu_ga_sid", String(v)); });
  } catch {}
}

export function getFullAttribution() {
  try {
    return {
      firstTouch: readJson(localStorage, "cu_attr_first"),
      lastTouch: readJson(localStorage, "cu_attr_last"),
      gaClientId: sessionStorage.getItem("cu_ga_cid") || null,
      gaSessionId: sessionStorage.getItem("cu_ga_sid") || null,
    };
  } catch { return { firstTouch: null, lastTouch: null, gaClientId: null, gaSessionId: null }; }
}

// Backward-compatible shape (existing consumers) + enriched campaign/click/GA fields.
export function getAttribution(extra = {}) {
  const p = new URLSearchParams(window.location.search);
  const last = readJson(localStorage, "cu_attr_last") || {};
  const first = readJson(localStorage, "cu_attr_first") || {};
  const ref = document.referrer || "";
  const pick = (k) => p.get(k) || last[k] || first[k] || "";

  let trafficSource = "direct";
  if (pick("utm_source")) trafficSource = pick("utm_source");
  else if (pick("gclid") || pick("gbraid") || pick("wbraid")) trafficSource = "google_ads";
  else if (pick("fbclid")) trafficSource = "facebook";
  else if (pick("msclkid")) trafficSource = "microsoft_ads";
  else {
    const r = ref || last.referrer || first.referrer || "";
    if (r) {
      try {
        const host = new URL(r).hostname.replace(/^www\./, "");
        if (host.includes("google")) trafficSource = "google";
        else if (host.includes("bing")) trafficSource = "bing";
        else if (/facebook|instagram|linkedin|t\.co|twitter|youtube/.test(host)) trafficSource = "social";
        else if (!host.includes(window.location.hostname)) trafficSource = "referral";
        else trafficSource = "internal";
      } catch { trafficSource = "referral"; }
    }
  }
  const ua = navigator.userAgent || "";
  const device = /Mobi|Android|iPhone|iPad/i.test(ua) ? "mobile" : "desktop";
  return {
    trafficSource,
    landingPage: window.location.pathname,
    searchQuery: p.get("q") || p.get("query") || "",
    device,
    referrer: ref.slice(0, 300),
    utmSource: pick("utm_source"),
    utmMedium: pick("utm_medium"),
    utmCampaign: pick("utm_campaign"),
    utmContent: pick("utm_content"),
    utmTerm: pick("utm_term"),
    gclid: pick("gclid"),
    gbraid: pick("gbraid"),
    wbraid: pick("wbraid"),
    fbclid: pick("fbclid"),
    msclkid: pick("msclkid"),
    ...getFullAttribution(),
    ...extra,
  };
}
